import tableExports from '@/tables/export/store/slice'
import { tableExportListenerMiddleware } from '@/tables/export/store/listeners'
import tableAnalysis from '@/tables/analysis/store/slice'
import { tableAnalysisListenerMiddleware } from '@/tables/analysis/store/listeners'
import { configureStore } from '@reduxjs/toolkit'
import chatUxSlice from '@/chat-common/store/chat-ux.slice'
import aiQueryResultSlice from './slices/ai-query-result.slice'
import globalToastSlice from './slices/global-toast.slice'
import uiStateSlice from './slices/ui-state.slice'
import { getSourceApi } from '@/chat-common/api/source-apis'
import { orgAdminApi } from '@/organizations/apis/org-admin.api'
import citatorStateSlice from '@/citator/store/citator-state.slice'
import checkoutReducer, { checkoutApi } from '@/checkout/store/slice'
import appInitSlice from './slices/app-init.slice'
import { chatUxListenerMiddleware } from '@/chat-common/store/chat-ux.listeners'
import agentV2ConversationsSlice from '@/agent-v2/conversations/store/slice'
import agentV2ReferencesSlice from '@/agent-v2/references/store/slice'
import agentV2TranscriptSlice from '@/agent-v2/store/slice'
import agentV2TransportSlice from '@/agent-v2/transport/store/slice'
import userSkillChatSlice from '@/user-skills/store/chat.slice'
import userSkillSelectionSlice from '@/user-skills/store/selection.slice'
import userSkillLibrarySlice from '@/user-skills/store/library.slice'
import { createUserSkillLibraryListenerMiddleware } from '@/user-skills/store/library.listeners'
import matterArtifactSelectionSlice from '@/agent-v2/artifact-selection/store/slice'
import agentConversationFormSlice from '@/agent-v2/chat-form/store/slice'
import { agentFormListenerMiddleware } from '@/agent-v2/chat-form/store/listeners'
import matterArtifactsSlice from '@/matter-artifacts/store/slice'
import artifactTemplatesSlice from '@/artifact-templates/store/slice'
import { agentV2ConversationsListenerMiddleware } from '@/agent-v2/conversations/store/listeners'
import { agentV2TransportListenerMiddleware } from '@/agent-v2/transport/store/listeners'
import agentV2MatterArtifactEditProposalsSlice from '@/agent-v2/artifact-edit-review/store/slice'
import { agentV2MatterArtifactEditProposalListenerMiddleware } from '@/agent-v2/artifact-edit-review/store/listeners'
import { matterArtifactListenerMiddleware } from '@/matter-artifacts/store/listeners'
import contentViewerSlice from '@/content-viewer/store/slice'
import draftingSlice from '@/drafting/store/slice'
import { draftingListenerMiddleware } from '@/drafting/store/listeners'
import matterActivityFeedSlice from '@/matter/activity-feed/store/slice'
import { matterActivityFeedListenerMiddleware } from '@/matter/activity-feed/store/listeners'
import draftWorkspaceSlice from '@/drafting/workspace/store/slice'
import { toastListenerMiddleware } from './slices/toast-middleware'
import mattersSlice from '@/matter/store/slice'
import { mattersListenerMiddleware } from '@/matter/store/listeners'
import conversationNavigationSlice from '@/chat-common/conversation-navigation/store/slice'
import responseFeedbackSlice from '@/response-feedback/store/slice'
import { responseFeedbackListenerMiddleware } from '@/response-feedback/store/listeners'
import accountReducer from '@/account/store/slice'
import { accountListenerMiddleware } from '@/account/store/listeners'

import { medSummaryEditListenerMiddleware } from '@/medical-billing-summaries/entries/store/entries-edit.listeners'
import medSummaryAssetSlice from '@/medical-billing-summaries/asset/store/slice'
import { medSummaryAssetListenerMiddleware } from '@/medical-billing-summaries/asset/store/listeners'
import { medSummaryEntryEditAnalyticsMiddleware } from '@/analytics/store/listeners/medsummary-entry-edit-analytics.listener'
import { unsupportedUploadAnalyticsMiddleware } from '@/analytics/store/listeners/unsupported-upload-analytics.listener'
import { agentV2ChatAnalyticsMiddleware } from '@/analytics/store/listeners/agent-v2-chat-analytics.listener'
import { agentV2InlineEditAnalyticsMiddleware } from '@/analytics/store/listeners/agent-v2-inline-edit-analytics.listener'
import { researchFiltersReducer } from '@/agent-v2/chat-form/sources-dialog/store/slice'
import { researchFiltersListenerMiddleware } from '@/agent-v2/chat-form/sources-dialog/store/listeners'
import medChronEventsSlice from '@/medical-chronology/events/store/slice'
import medChronReferencesSlice from '@/medical-chronology/references/store/slice'
import medChronAssetSlice from '@/medical-chronology/asset/store/slice'
import { medChronAssetListenerMiddleware } from '@/medical-chronology/asset/store/listeners'
import timelineEventsSlice from '@/timeline/events/store/slice'
import timelineReferencesSlice from '@/timeline/references/store/slice'
import timelineAssetSlice from '@/timeline/asset/store/slice'
import { timelineAssetListenerMiddleware } from '@/timeline/asset/store/listeners'
import uppyFileUploadSlice from '@/uppy-file-upload/store/slice'
import { uppyFileUploadListenerMiddleware } from '@/uppy-file-upload/store/listeners'
import userFoldersSlice from '@/user_files/folders/store/slice'
import { userFoldersListenerMiddleware } from '@/user_files/folders/store/listeners'
import userFilesSlice from '@/user_files/files/store/slice'
import { userFilesListenerMiddleware } from '@/user_files/files/store/listeners'
import userFilesPollingSlice from '@/user_files/polling/store/slice'
import { userFilesPollingListenerMiddleware } from '@/user_files/polling/store/listeners'
import userFilesBatchingSlice from '@/user_files/batching/store/slice'
import { userFilesBatchingListenerMiddleware } from '@/user_files/batching/store/listeners'
import externalStorageSlice from '@/user_files/external-storage/store/slice'
import medSummaryEntriesSlice from '@/medical-billing-summaries/entries/store/entries.slice'
import medSummaryEntryEditSlice from '@/medical-billing-summaries/entries/store/entries-edit.slice'
import medSummaryReferencesSlice from '@/medical-billing-summaries/references/store/slice'
import ocrQuotaUiSlice from '@/ocr_quota/store/slice'

export const store = configureStore({
  reducer: {
    tableAnalysis,
    tableExports,
    agentV2ConversationsState: agentV2ConversationsSlice,
    agentV2ReferencesState: agentV2ReferencesSlice,
    agentV2TranscriptState: agentV2TranscriptSlice,
    agentV2TransportState: agentV2TransportSlice,
    userSkillSelectionState: userSkillSelectionSlice,
    userSkillChatState: userSkillChatSlice,
    userSkillLibraryState: userSkillLibrarySlice,
    agentV2MatterArtifactEditProposalsState: agentV2MatterArtifactEditProposalsSlice,
    matterArtifactSelectionState: matterArtifactSelectionSlice,
    agentConversationFormState: agentConversationFormSlice,
    researchFiltersState: researchFiltersReducer,
    matterArtifactsState: matterArtifactsSlice,
    artifactTemplatesState: artifactTemplatesSlice,
    contentViewerState: contentViewerSlice,
    draftingState: draftingSlice,
    matterActivityFeedState: matterActivityFeedSlice,
    draftWorkspaceState: draftWorkspaceSlice,
    chatUxState: chatUxSlice,
    aiQueryResults: aiQueryResultSlice,
    globalToast: globalToastSlice,
    uiState: uiStateSlice,
    appInitState: appInitSlice,
    citatorState: citatorStateSlice,
    medChronEventsState: medChronEventsSlice,
    medChronReferencesState: medChronReferencesSlice,
    medChronAssetState: medChronAssetSlice,
    timelineEventsState: timelineEventsSlice,
    timelineReferencesState: timelineReferencesSlice,
    timelineAssetState: timelineAssetSlice,
    medSummaryAssetState: medSummaryAssetSlice,
    medSummaryEntriesState: medSummaryEntriesSlice,
    medSummaryEntryEditState: medSummaryEntryEditSlice,
    medSummaryReferencesState: medSummaryReferencesSlice,
    uppyFileUploadState: uppyFileUploadSlice,
    userFoldersState: userFoldersSlice,
    userFilesState: userFilesSlice,
    userFilesPollingState: userFilesPollingSlice,
    userFilesBatchingState: userFilesBatchingSlice,
    externalStorageState: externalStorageSlice,
    responseFeedbackState: responseFeedbackSlice,
    checkoutState: checkoutReducer,
    mattersState: mattersSlice,
    conversationNavigationState: conversationNavigationSlice,
    accountState: accountReducer,
    ocrQuotaUiState: ocrQuotaUiSlice,
    [checkoutApi.reducerPath]: checkoutApi.reducer,
    // Add generated RTK Query reducers
    [getSourceApi.reducerPath]: getSourceApi.reducer,
    [orgAdminApi.reducerPath]: orgAdminApi.reducer,
  },

  // Add createListenerMiddleware and RTK-Query Middleware
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(getSourceApi.middleware, orgAdminApi.middleware, checkoutApi.middleware)
      // Prepend listener middleware because it must be started before other middleware
      .prepend(
        matterArtifactListenerMiddleware.middleware,
        researchFiltersListenerMiddleware.middleware,
        agentFormListenerMiddleware.middleware,
        agentV2ConversationsListenerMiddleware.middleware,
        agentV2TransportListenerMiddleware.middleware,
        createUserSkillLibraryListenerMiddleware().middleware,
        agentV2MatterArtifactEditProposalListenerMiddleware.middleware,
        mattersListenerMiddleware.middleware,
        draftingListenerMiddleware.middleware,
        tableAnalysisListenerMiddleware.middleware,
        tableExportListenerMiddleware.middleware,
        matterActivityFeedListenerMiddleware.middleware,
        chatUxListenerMiddleware.middleware,
        toastListenerMiddleware.middleware,
        responseFeedbackListenerMiddleware.middleware,
        medChronAssetListenerMiddleware.middleware,
        timelineAssetListenerMiddleware.middleware,
        medSummaryEditListenerMiddleware.middleware,
        medSummaryAssetListenerMiddleware.middleware,
        accountListenerMiddleware.middleware,
        medSummaryEntryEditAnalyticsMiddleware.middleware,
        unsupportedUploadAnalyticsMiddleware.middleware,
        agentV2ChatAnalyticsMiddleware.middleware,
        agentV2InlineEditAnalyticsMiddleware.middleware,
        uppyFileUploadListenerMiddleware.middleware,
        userFoldersListenerMiddleware.middleware,
        userFilesListenerMiddleware.middleware,
        userFilesPollingListenerMiddleware.middleware,
        userFilesBatchingListenerMiddleware.middleware
      ),
})
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch
